import { useState } from "react";
import { useUIStore } from "../../store/uiStore";
import type { Basemap } from "../../store/uiStore";
import { headerPalette } from "../../lib/headerTheme";
import { useIsMobile } from "../../lib/useIsMobile";

interface Option {
  key: Basemap;
  label: string;
  description: string;
}

const OPTIONS: Option[] = [
  {
    key: "street",
    label: "Map",
    description: "OpenStreetMap — roads, towns, and place labels",
  },
  {
    key: "satellite",
    label: "Live",
    description:
      "Esri World Imagery — high-resolution photo, but always shows the latest available imagery (does NOT follow the selected tick date)",
  },
  {
    key: "satellite-time",
    label: "By date",
    description:
      "NASA GIBS MODIS — daily true-color satellite, automatically follows the selected tick date. Lower resolution (~250 m); some days may be cloudy.",
  },
];

export function BasemapSwitcher() {
  const theme = useUIStore((s) => s.theme);
  const basemap = useUIStore((s) => s.basemap);
  const setBasemap = useUIStore((s) => s.setBasemap);
  const riskOverlayVisible = useUIStore((s) => s.riskOverlayVisible);
  const toggleRiskOverlay = useUIStore((s) => s.toggleRiskOverlay);
  const palette = headerPalette(theme);
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);

  const current = OPTIONS.find((o) => o.key === basemap);

  return (
    <div
      style={{
        position: "absolute",
        bottom: "calc(28px + env(safe-area-inset-bottom, 0px))",
        right: 12,
        zIndex: 600,
        background: palette.buttonBg,
        border: `1px solid ${palette.buttonBorder}`,
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
        fontFamily: "'Inter', system-ui, sans-serif",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "stretch" }}>
        {!isMobile && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0 8px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: palette.statLabel,
              borderRight: `1px solid ${palette.buttonBorder}`,
              background: theme === "dark" ? "#0f172a" : "#f3f4f6",
            }}
          >
            Basemap
          </div>
        )}
        {OPTIONS.map((opt, i) => {
          const isActive = basemap === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => setBasemap(opt.key)}
              title={opt.description}
              aria-pressed={isActive}
              style={{
                padding: isMobile ? "8px 10px" : "6px 12px",
                background: isActive
                  ? theme === "dark"
                    ? "#1d4ed8"
                    : "#dbeafe"
                  : "transparent",
                color: isActive
                  ? theme === "dark"
                    ? "#f9fafb"
                    : "#1d4ed8"
                  : palette.logo,
                border: "none",
                borderLeft: i === 0 ? "none" : `1px solid ${palette.buttonBorder}`,
                fontSize: 12,
                fontWeight: isActive ? 600 : 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {opt.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={toggleRiskOverlay}
          title={
            riskOverlayVisible
              ? "Hide risk overlay (cell points + county polygons)"
              : "Show risk overlay"
          }
          aria-pressed={!riskOverlayVisible}
          style={{
            padding: "0 10px",
            background: riskOverlayVisible
              ? "transparent"
              : theme === "dark"
                ? "#7c2d12"
                : "#fee2e2",
            color: riskOverlayVisible
              ? palette.logo
              : theme === "dark"
                ? "#fecaca"
                : "#991b1b",
            border: "none",
            borderLeft: `1px solid ${palette.buttonBorder}`,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {riskOverlayVisible ? <EyeIcon /> : <EyeOffIcon />}
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? "Hide description" : "Show description"}
          aria-expanded={expanded}
          style={{
            padding: "0 8px",
            background: "transparent",
            color: palette.statLabel,
            border: "none",
            borderLeft: `1px solid ${palette.buttonBorder}`,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {expanded ? "▾" : "▸"}
        </button>
      </div>
      {expanded && current && (
        <div
          style={{
            padding: "8px 10px",
            fontSize: 11,
            lineHeight: 1.4,
            color: palette.statLabel,
            borderTop: `1px solid ${palette.buttonBorder}`,
            maxWidth: 320,
            background: theme === "dark" ? "#0f172a" : "#f9fafb",
          }}
        >
          <div style={{ marginBottom: 6 }}>
            <strong style={{ color: palette.logo }}>{current.label}:</strong> {current.description}
          </div>
          <div style={{ color: palette.statLabel }}>
            <strong style={{ color: palette.logo }}>Eye icon:</strong>{" "}
            toggle the risk overlay (cell points and choropleth) to inspect the
            basemap underneath.
          </div>
        </div>
      )}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
